export interface RecipeHistoryChange {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

interface HistoryInput {
  action: string;
  before_data: unknown;
  after_data: unknown;
}

export function historyRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

function nullableText(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isChange(value: unknown): value is RecipeHistoryChange {
  const row = historyRecord(value);
  return typeof row.field === "string" && typeof row.label === "string"
    && nullableText(row.before) && nullableText(row.after);
}

function versionText(value: unknown): string | null {
  return (typeof value === "number" && Number.isFinite(value)) || typeof value === "string"
    ? String(value) : null;
}

/** Only display recorded values; request keys and replacement flags are not diffs. */
export function describeRecipeHistory(entry: HistoryInput): {
  changes: RecipeHistoryChange[];
  note: string | null;
  message: string | null;
} {
  const before = historyRecord(entry.before_data);
  const after = historyRecord(entry.after_data);
  const note = typeof after.change_summary === "string" && after.change_summary.trim()
    ? after.change_summary : null;
  const changes: RecipeHistoryChange[] = [];

  if (entry.action === "update_draft") {
    if (after.details_version === 1 && Array.isArray(after.changes) && after.changes.every(isChange)) {
      changes.push(...after.changes.filter(change => change.before !== change.after));
      return { changes, note, message: changes.length ? null : "Saved without recipe field changes." };
    }
    return { changes, note, message: after.details_version === undefined
      ? "Exact changes were not recorded for this older entry."
      : "Change details are unavailable for this entry." };
  }

  const add = (field: string, label: string, oldValue: unknown, newValue: unknown) => {
    if (nullableText(oldValue) && nullableText(newValue) && oldValue !== newValue) {
      changes.push({ field, label, before: oldValue, after: newValue });
    }
  };
  for (const [field, label] of [["status", "Recipe status"], ["service_status", "Service status"]]) {
    add(field, label, before[field], after[field]);
  }
  if (entry.action === "update_aliases" && Array.isArray(before.aliases) && Array.isArray(after.aliases)
    && before.aliases.every(value => typeof value === "string")
    && after.aliases.every(value => typeof value === "string")) {
    const oldAliases = [...before.aliases].sort();
    const newAliases = [...after.aliases].sort();
    if (JSON.stringify(oldAliases) !== JSON.stringify(newAliases)) {
      changes.push({ field: "aliases", label: "Search aliases", before: oldAliases.join("\n") || null, after: newAliases.join("\n") || null });
    }
  }
  if (entry.action === "create_drink") {
    const drink = historyRecord(after.drink);
    const version = historyRecord(after.version);
    add("name", "Drink name", null, drink.name);
    add("version", "Recipe version", null, versionText(version.version_number));
  }
  if (entry.action === "create_draft_from_approved" || entry.action === "restore_as_draft") {
    const oldVersion = versionText(before.source_version_number);
    const newVersion = versionText(after.new_version_number);
    if (oldVersion !== null && newVersion !== null) add("version", "Recipe version", oldVersion, newVersion);
  }
  return { changes, note, message: changes.length ? null : "Change details were not recorded for this entry." };
}
